import { useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";

const PROGRAM_NAME = { AI: "Artificial Intelligence", Robotics: "Robotics & Humanoids", UAV: "UAV / Drone Technology", Aerospace: "Aerospace Engineering" };
const PROGRAM_HI = { AI: "आर्टिफिशियल इंटेलिजेंस", Robotics: "रोबोटिक्स और ह्यूमनॉइड्स", UAV: "UAV / ड्रोन टेक्नोलॉजी", Aerospace: "एयरोस्पेस इंजीनियरिंग" };

const CertDoc = ({ r, innerRef }) => {
  const date = new Date(r.completed_at || Date.now()).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const pct = r.sessions ? Math.round((r.present / r.sessions) * 100) : 0;
  const done = r.projects.filter((p) => p.status === "completed").length;
  return (
    <div ref={innerRef} style={{ width: 1123, height: 794, background: "#fff", fontFamily: "'Outfit','Noto Sans Devanagari',sans-serif", position: "relative", boxSizing: "border-box", padding: 28 }}>
      <div style={{ border: "3px solid #0A192F", height: "100%", boxSizing: "border-box", padding: 8 }}>
        <div style={{ border: "1px solid #FF2E63", height: "100%", boxSizing: "border-box", padding: "40px 70px", textAlign: "center", position: "relative",
          background: "radial-gradient(600px 300px at 0% 0%, rgba(255,46,99,0.08), transparent), radial-gradient(600px 300px at 100% 100%, rgba(0,229,255,0.10), transparent)" }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#0A192F" }}><span style={{ color: "#FF2E63" }}>Robo</span>Spark <span style={{ fontSize: 12, letterSpacing: 3, color: "#64748b" }}>PRP PVT LTD</span></div>
          <div style={{ marginTop: 26, fontSize: 44, fontWeight: 900, color: "#0A192F", letterSpacing: 2 }}>CERTIFICATE OF COMPLETION</div>
          <div style={{ fontSize: 18, color: "#FF2E63", fontWeight: 700 }}>पूर्णता प्रमाणपत्र</div>
          <div style={{ marginTop: 28, fontSize: 16, color: "#475569" }}>This is proudly presented to · यह प्रमाणपत्र गर्व से प्रदान किया जाता है</div>
          <div style={{ marginTop: 10, fontSize: 48, fontWeight: 800, color: "#0A192F", borderBottom: "2px solid #FF2E63", display: "inline-block", padding: "0 30px 6px" }}>{r.student_name}</div>
          <div style={{ marginTop: 18, fontSize: 16, color: "#334155", lineHeight: 1.7 }}>
            of Class {r.class_grade}, {r.school_name}, for successfully completing the RoboSpark<br />
            <b style={{ fontSize: 22, color: "#0A192F" }}>{PROGRAM_NAME[r.program] || r.program}</b> program · <span>{PROGRAM_HI[r.program]} कार्यक्रम सफलतापूर्वक पूर्ण किया</span>
          </div>
          <div style={{ marginTop: 22, display: "flex", justifyContent: "center", gap: 40, fontSize: 14, color: "#0A192F" }}>
            <div><b style={{ fontSize: 22 }}>{pct}%</b><br />Attendance · उपस्थिति</div>
            <div><b style={{ fontSize: 22 }}>{done}</b><br />Projects completed · प्रोजेक्ट</div>
          </div>
          <div style={{ position: "absolute", left: 70, right: 70, bottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: 13, color: "#334155" }}>
            <div style={{ textAlign: "left" }}>Date: <b>{date}</b><br />Certificate No: <b>{r.certificate_no}</b><br /><span style={{ fontSize: 11 }}>Verify at {window.location.host}/verify-certificate</span></div>
            <div style={{ width: 90, height: 90, borderRadius: "50%", background: "#FF2E63", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, lineHeight: 1.2, boxShadow: "0 0 0 6px #fff, 0 0 0 8px #FF2E63" }}>ROBO<br />SPARK</div>
            <div style={{ textAlign: "right" }}><div style={{ borderTop: "1px solid #0A192F", paddingTop: 4, width: 200 }}>Director, RoboSpark PrP Pvt Ltd</div><span style={{ fontSize: 11 }}>C-30, C-Block, Sector 63, Noida-201301 (U.P.)</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CertificateButton = ({ r }) => {
  const { L } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: 0,
        filename: `RoboSpark-Certificate-${r.student_name.replace(/\s+/g, "-")}-${r.certificate_no}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "px", format: [1123, 794], orientation: "landscape", hotfixes: ["px_scaling"] },
      }).from(ref.current).save();
    } catch { toast.error(L("Could not create certificate.", "प्रमाणपत्र नहीं बन सका।")); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 text-[#0A192F] rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="certificate-card">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-[#FF3B3B] text-white grid place-items-center"><Award className="w-6 h-6" /></div>
        <div>
          <div className="font-extrabold text-lg">{L("Program completed!", "कार्यक्रम पूरा हुआ!")}</div>
          <div className="text-slate-500 text-sm">{L("Certificate no.", "प्रमाणपत्र संख्या")} {r.certificate_no}</div>
        </div>
      </div>
      <Button onClick={download} disabled={busy} className="rs-cta rounded-full" data-testid="certificate-download-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Download certificate (PDF)", "प्रमाणपत्र डाउनलोड करें (PDF)")}
      </Button>
      <div aria-hidden style={{ position: "fixed", left: -12000, top: 0 }}><CertDoc r={r} innerRef={ref} /></div>
    </div>
  );
};
